/**
 * Per-route metadata registry — the "template" layer.
 *
 * `ROUTE_META` maps each public, indexable path to its unique title/description
 * (+ optional structured data). `RouteSeo` looks a page up here on every
 * navigation and applies it; anything NOT listed resolves to a `noindex`
 * default, so private app pages (dashboards, admin, onboarding, item/outfit
 * detail, checkout, and the numbered /landingN design experiments) are kept out
 * of the index automatically without touching each page.
 *
 * A few content pages own their <Seo> directly because their metadata depends on
 * data only the page has (the FAQ answers, a blog post row). Those paths are in
 * `DEFER_PATHS`; `resolveRouteMeta` returns `{ defer: true }` for them so the
 * central manager stays out of the way.
 */
import type { SeoProps } from "./Seo";
import {
  breadcrumbLd,
  cityAreaServed,
  faqPageLd,
  howToLd,
  organizationWithReviewsLd,
  personLd,
  serviceLd,
  websiteLd,
} from "./jsonld";
import { TESTIMONIALS } from "@/data/testimonials";
import { FAQS } from "@/data/faqs";

export interface RouteMeta {
  title: string;
  description: string;
  ogType?: string;
  jsonLd?: object | object[];
}

// Home page emits the brand entity enriched with Review + AggregateRating so
// testimonials show up in Rich Results and can be cited by AI answer engines.
// The FAQ section is rendered on the home page too, so its FAQPage schema rides along.
const homeLd = [organizationWithReviewsLd(TESTIMONIALS), websiteLd(), serviceLd(), faqPageLd(FAQS)];

/** Shared HowTo steps for "how Cello works" (mirrors the /howtoguide page + llms.txt). */
const HOW_CELLO_WORKS_STEPS = [
  {
    name: "Digitize your closet",
    text: "On a quick onboarding call we show you how to capture your wardrobe in under 30 minutes. Our AI then auto-tags and organizes every item into a beautiful digital closet.",
  },
  {
    name: "Meet your stylist",
    text: "You're paired with an expert human stylist (trained at schools like Parsons, FIT, and London College of Fashion) who learns your taste, lifestyle, and goals.",
  },
  {
    name: "Receive your outfits",
    text: "Your stylist builds a personalized lookbook of outfits from the clothes you already own, plus a wardrobe gap analysis and curated shopping recommendations for what's missing.",
  },
];

/** Home → segment. Second-level pages get a two-step trail. */
const twoStepCrumb = (name: string, path: string) =>
  breadcrumbLd([
    { name: "Home", path: "/" },
    { name, path },
  ]);

/**
 * Every indexable public route with its unique metadata.
 * Keys are exact, lower-case pathnames (paths are normalised to lower-case app-wide).
 */
export const ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    title: "Cello: Your hyper-personalized stylist",
    description:
      "Cello pairs you with a real expert stylist, powered by AI, to create hyper-personalized outfit recommendations from your own wardrobe.",
    jsonLd: homeLd,
  },

  // --- Company / content ---
  "/about": {
    title: "About Cello — real stylists, powered by AI",
    description:
      "Meet Cello: a personal styling service that pairs you with an expert human stylist and AI to build outfits from the wardrobe you already own.",
    jsonLd: [
      personLd({ name: "Jinny Chen", jobTitle: "Co-founder" }),
      personLd({ name: "Caden Broussard", jobTitle: "Co-founder" }),
      twoStepCrumb("About", "/about"),
    ],
  },
  "/stylist": {
    title: "Work with a Cello stylist — personal styling",
    description:
      "Get paired with a professional Cello stylist who learns your taste, shops for you, and builds outfits from your closet. Personal styling, reimagined.",
    jsonLd: [serviceLd({ name: "Cello personal stylist" }), twoStepCrumb("Stylist", "/stylist")],
  },
  "/contact": {
    title: "Contact Cello",
    description:
      "Get in touch with the Cello team — questions about personal styling, your account, press, or partnerships.",
  },
  "/blog": {
    title: "The Cello Journal — style guides & menswear advice",
    description:
      "Style guides, wardrobe advice, and menswear inspiration from the expert stylists at Cello.",
    jsonLd: twoStepCrumb("Blog", "/blog"),
  },
  "/style-lookbook": {
    title: "Cello Lookbook — outfit inspiration",
    description:
      "Browse real outfits styled by Cello experts for inspiration across seasons, occasions, and personal styles.",
  },
  "/manifesto": {
    title: "The Cello Manifesto",
    description:
      "What we believe about style, confidence, and dressing well — the Cello manifesto.",
  },
  "/howtoguide": {
    title: "How Cello works — your personal styling guide",
    description:
      "A step-by-step guide to getting styled with Cello: build your digital closet, meet your stylist, and receive outfits made for you.",
    jsonLd: [
      howToLd({
        name: "How Cello works",
        description:
          "How to get styled with Cello: digitize your closet, meet your stylist, and receive personalized outfits.",
        steps: HOW_CELLO_WORKS_STEPS,
      }),
      twoStepCrumb("How it works", "/howtoguide"),
    ],
  },
  "/press": {
    title: "Cello in the press",
    description:
      "Media coverage and press mentions of Cello — the personal styling service pairing you with an expert human stylist and AI.",
  },

  // --- Legal ---
  "/privacy-policy": {
    title: "Privacy Policy | Cello",
    description: "How Cello collects, uses, and protects your personal information.",
  },
  "/terms": {
    title: "Terms of Service | Cello",
    description: "The terms and conditions for using Cello's personal styling service.",
  },
  "/sms": {
    title: "SMS Policy | Cello",
    description: "Cello Closet's text-message program: what we send, how often, and how to opt out.",
  },
  "/smstermsandconditions": {
    title: "SMS Terms and Conditions | Cello",
    description: "The terms for Cello Closet's SMS program, including consent, frequency, rates and opt-out.",
  },
  "/sms-signup": {
    title: "Text me style tips | Cello",
    description: "Opt in to style tips, outfit ideas and occasional promotions by text from Cello Closet.",
  },

  // --- City pages (local intent) ---
  "/sanfrancisco": {
    title: "Personal stylist in San Francisco | Cello",
    description:
      "Cello brings expert personal styling to San Francisco. Get paired with a stylist who builds outfits from your wardrobe — no shopping stress.",
    jsonLd: [
      serviceLd({ name: "Personal styling in San Francisco", areaServed: cityAreaServed("San Francisco", "California") }),
      twoStepCrumb("San Francisco", "/sanfrancisco"),
    ],
  },
  "/chicago": {
    title: "Personal stylist in Chicago | Cello",
    description:
      "Expert personal styling for Chicago. Cello pairs you with a stylist who curates outfits from your closet and shops the gaps.",
    jsonLd: [
      serviceLd({ name: "Personal styling in Chicago", areaServed: cityAreaServed("Chicago", "Illinois") }),
      twoStepCrumb("Chicago", "/chicago"),
    ],
  },

  // --- Audience / campaign pages ---
  "/men": {
    title: "Personal styling for men | Cello",
    description:
      "Menswear styling made simple. Cello pairs you with an expert stylist to build outfits from your wardrobe and upgrade your look.",
    jsonLd: [serviceLd({ name: "Personal styling for men" }), twoStepCrumb("Men", "/men")],
  },
  "/wardrobe": {
    title: "Build a wardrobe that works | Cello",
    description:
      "Stop guessing. Cello's stylists turn the clothes you own into outfits you'll actually wear — and fill the gaps intentionally.",
    jsonLd: twoStepCrumb("Wardrobe", "/wardrobe"),
  },
  "/body-type": {
    title: "Dress for your body type | Cello",
    description:
      "Get styling tailored to your build. Cello's expert stylists create outfits that fit and flatter, using your own wardrobe.",
    jsonLd: twoStepCrumb("Body Type", "/body-type"),
  },
  "/style-help": {
    title: "Need style help? Get a personal stylist | Cello",
    description:
      "Overwhelmed by fashion? Cello pairs you with a real stylist who handles the hard part — outfits, shopping, and confidence, sorted.",
    jsonLd: twoStepCrumb("Style Help", "/style-help"),
  },
  "/redefine-style": {
    title: "Redefine your style with Cello",
    description:
      "Ready for a change? Cello's expert stylists help you redefine your look with outfits built from your wardrobe and a few smart additions.",
  },
  "/modern-comfort": {
    title: "Modern comfort styling | Cello",
    description:
      "Look sharp without sacrificing comfort. Cello styles elevated, easy outfits from the clothes you already own.",
  },
  "/offduty": {
    title: "Off-duty style, handled | Cello",
    description:
      "Nail your weekend and off-duty looks. Cello's stylists build relaxed, put-together outfits from your wardrobe.",
  },
  "/newrole": {
    title: "Dress for the new role | Cello",
    description:
      "New job or leadership role? Cello's stylists build a wardrobe that matches where you're headed — starting with what you already own.",
  },
  "/linkedin": {
    title: "Personal styling for professionals | Cello",
    description:
      "Show up sharp at work and beyond. Cello pairs professionals with expert stylists who build outfits from their own wardrobe.",
  },
  "/dreamforce": {
    title: "Cello styling for Dreamforce",
    description:
      "Headed to Dreamforce? Cello's stylists help you look the part — polished, comfortable outfits built for the week.",
  },
  "/promotion": {
    title: "Cello personal styling — special offer",
    description:
      "Get paired with an expert Cello stylist. Personal styling that builds outfits from your wardrobe and shops the gaps for you.",
  },
};

/**
 * Public, indexable paths whose <Seo> is rendered by the page itself (their
 * metadata needs data only the page has). Central RouteSeo defers to them.
 */
export const DEFER_PATHS = {
  exact: new Set<string>(["/faq"]),
  prefixes: ["/blog/"], // individual blog posts; "/blog" (the list) stays central
};

/** All indexable URLs, for the sitemap. Blog posts are added dynamically elsewhere. */
export const INDEXABLE_PATHS: string[] = [
  ...Object.keys(ROUTE_META),
  "/faq",
];

const isDeferred = (pathname: string): boolean => {
  if (DEFER_PATHS.exact.has(pathname)) return true;
  return DEFER_PATHS.prefixes.some((p) => pathname.startsWith(p) && pathname !== p.replace(/\/$/, ""));
};

/**
 * Resolve the head config for a pathname:
 * - deferred paths (FAQ, blog posts) → `null`: the page renders its own <Seo>
 * - registered public paths → their unique metadata (indexable)
 * - anything else → a noindex fallback (private/app/experimental routes)
 */
export function resolveRouteMeta(pathname: string): SeoProps | null {
  if (isDeferred(pathname)) return null;

  const meta = ROUTE_META[pathname];
  if (meta) {
    return {
      title: meta.title,
      description: meta.description,
      canonical: pathname,
      index: true,
      ogType: meta.ogType ?? "website",
      jsonLd: meta.jsonLd ?? null,
    };
  }

  // Fallback keeps the brand title but tells engines not to index app pages.
  return {
    index: false,
    canonical: pathname,
    jsonLd: null,
  };
}
